import React from "react";

import {
  BcItem,
  CatalogDiffSyncChannel,
  formatItemVariant,
  Item,
  ItemVariant,
  LegacyMegaraxPrice,
  PriceList,
  PriceSheetRow,
  ShopifyPrice,
  ShopifyProduct,
  ShopifyProductVariant,
} from "@megaron/catalog-contracts";
import {
  DiffSyncDependentChannelLink,
  DiffSyncDependentChannelRefreshResult,
  DiffSyncPage,
  DiffSyncRecord,
} from "@megaron/diff-sync-webapp";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";

type DiffValue = Record<string, unknown>;

type Props<T extends DiffValue> = {
  title: string;
  channel: CatalogDiffSyncChannel;
  directionLabels: {
    pull: string;
    push: string;
  };
  labels: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, (value: unknown) => React.ReactNode>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
};

export const BcItemSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<BcItem & DiffValue>
    title="Synchronizacja Business Central"
    channel="bcItem"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do BC",
    }}
    alwaysVisibleColumns={["itemName"]}
    labels={{
      bcId: "Id BC",
      itemId: "Id",
      itemName: "Nazwa",
      gtin: "EAN",
    }}
  />
);

export const SheetsItemSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<Item> & DiffValue>
    title="Synchronizacja Google Sheets"
    channel="sheetsItem"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["itemName"]}
    labels={itemLabels}
    formatters={itemFormatters}
  />
);

export const ShopifyProductSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyProduct> & DiffValue>
    title="Synchronizacja Shopify - produkty"
    channel="shopifyProduct"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["handle", "title"]}
    labels={shopifyProductLabels}
  />
);

export const ShopifyVariantSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyProductVariant> & DiffValue>
    title="Synchronizacja Shopify - warianty"
    channel="shopifyVariant"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["sku", "variant"]}
    labels={shopifyVariantLabels}
    formatters={shopifyVariantFormatters}
  />
);

export const ShopifyPriceSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<ShopifyPrice> & DiffValue>
    title="Synchronizacja Shopify - ceny"
    channel="shopifyPrice"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do Shopify",
    }}
    alwaysVisibleColumns={["itemId", "itemName"]}
    labels={shopifyPriceLabels}
    formatters={shopifyPriceFormatters}
  />
);

export const LegacyMegaraxPriceSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<LegacyMegaraxPrice> & DiffValue>
    title="Migracja cen z legacy Megarax"
    channel="legacyMegaraxPrice"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do legacy Megarax",
    }}
    alwaysVisibleColumns={["priceList", "itemId", "itemName", "gtin", "activeFrom"]}
    labels={legacyMegaraxPriceLabels}
    formatters={legacyMegaraxPriceFormatters}
  />
);

export const SheetsPriceListSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<PriceList & DiffValue>
    title="Synchronizacja Google Sheets - cenniki"
    channel="sheetsPriceList"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["name"]}
    labels={priceListLabels}
  />
);

export const SheetsPriceSyncDiffPage = () => (
  <CatalogItemSyncDiffPage<Partial<PriceSheetRow> & DiffValue>
    title="Synchronizacja Google Sheets - ceny"
    channel="sheetsPrice"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    alwaysVisibleColumns={["priceList", "itemId", "itemName", "activeFrom"]}
    labels={priceLabels}
    formatters={priceFormatters}
  />
);

const CatalogItemSyncDiffPage = <T extends DiffValue>(props: Props<T>) => {
  const client = useClientManager("catalog");
  const serviceClient = useServiceClient("catalog");

  const pullQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPullable: true,
  });

  const pushQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPushable: true,
  });

  const allDiffsQuery = client.diffQuery().useQuery({
    channel: props.channel,
  });
  const channelQuery = client.diffChannelQuery().useQuery({
    channel: props.channel,
  });

  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ channel: props.channel });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    try {
      await pullMutation.mutateAsync({ channel: props.channel, diffIds });
    } finally {
      await refetchDiffs();
    }
  };

  const push = async (diffIds: string[]) => {
    try {
      await pushMutation.mutateAsync({ channel: props.channel, diffIds });
    } finally {
      await refetchDiffs();
    }
  };

  const pullDependentChannels = React.useMemo(
    () =>
      channelQuery.data?.pullDependentChannels?.map((channel) => ({
        channel: channel.channel,
        name: channel.name,
        href: getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: channel.channel }),
      })),
    [channelQuery.data?.pullDependentChannels],
  );

  const pushDependentChannels = React.useMemo(
    () =>
      channelQuery.data?.pushDependentChannels?.map((channel) => ({
        channel: channel.channel,
        name: channel.name,
        href: getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: channel.channel }),
      })),
    [channelQuery.data?.pushDependentChannels],
  );

  const refreshDependentChannel = async (
    channel: DiffSyncDependentChannelLink,
  ): Promise<DiffSyncDependentChannelRefreshResult> => {
    try {
      const refreshResult = await serviceClient.refreshDiffs({ channel: channel.channel as CatalogDiffSyncChannel });
      if (refreshResult.isFailure) throw new Error(formatClientError(refreshResult.error));

      const diffResult = await serviceClient.diffQuery({ channel: channel.channel as CatalogDiffSyncChannel });
      if (diffResult.isFailure) throw new Error(formatClientError(diffResult.error));

      const diffs = diffResult.value as DiffSyncRecord<DiffValue>[];
      return {
        ...channel,
        changeCount: diffs.filter(isChangeDiff).length,
        errorCount: diffs.filter(isErrorDiff).length,
      };
    } catch (error) {
      return {
        ...channel,
        error: formatClientError(error),
      };
    }
  };

  return (
    <DiffSyncPage<T>
      title={props.title}
      pullableDiffs={pullQuery.data as DiffSyncRecord<T>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<T>[] | undefined}
      errorDiffs={allDiffsQuery.data?.filter(isErrorDiff) as DiffSyncRecord<T>[] | undefined}
      directionLabels={props.directionLabels}
      labels={props.labels}
      formatters={props.formatters}
      alwaysVisibleColumns={props.alwaysVisibleColumns}
      remoteResourceUrl={channelQuery.data?.remoteResourceUrl}
      pullDependentChannels={pullDependentChannels}
      pushDependentChannels={pushDependentChannels}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
      onRefreshDependentChannel={refreshDependentChannel}
    />
  );
};

const isErrorDiff = (diff: DiffSyncRecord<DiffValue>) => !!diff.error;
const isChangeDiff = (diff: DiffSyncRecord<DiffValue>) => !diff.error && (diff.isPullable || diff.isPushable);

const formatClientError = (error: unknown) => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;

  return JSON.stringify(error) ?? String(error);
};

const itemLabels = {
  bcId: "Id BC",
  shopifyProductId: "Id produktu Shopify",
  box: "Karton",
  brand: "Marka",
  cost: "Koszt STD",
  description: "Opis",
  gtin: "EAN",
  handle: "Handle",
  isArchived: "Zarchiwizowany?",
  isInAvailability: "Śledzenie dostępności",
  itemId: "Id",
  itemName: "Nazwa",
  layer: "Warstwa",
  loyaltyId: "Id Loyalty",
  massKg: "Masa kg",
  pallet: "Paleta",
  productGroup: "Grupa produktowa",
  productId: "Indeks produktu",
  size: "Wielkość",
  sizeUnit: "Jednostka sprzedaży",
  title: "Tytuł",
  variant: "Wariant",
  shopifyVariantId: "variantId",
  coverageM2: "Pokrycie m2",
} satisfies Partial<Record<keyof Item, string>>;

const itemFormatters = {
  variant: (value) => formatItemVariant(value as ItemVariant) || "-",
} satisfies Partial<Record<keyof Item, (value: unknown) => React.ReactNode>>;

const shopifyProductLabels = {
  id: "Id Shopify",
  title: "Tytuł",
  description: "Opis",
  vendor: "Marka",
  handle: "Handle",
  tags: "Tagi",
} satisfies Partial<Record<keyof ShopifyProduct, string>>;

const shopifyVariantLabels = {
  variantId: "Id wariantu",
  sku: "SKU",
  productId: "Id produktu",
  variant: "Wariant",
  barcode: "EAN",
  weightKg: "Masa kg",
} satisfies Partial<Record<keyof ShopifyProductVariant, string>>;

const shopifyVariantFormatters = {
  variant: (value) => formatItemVariant(value as ItemVariant) || "-",
} satisfies Partial<Record<keyof ShopifyProductVariant, (value: unknown) => React.ReactNode>>;

const shopifyPriceLabels = {
  itemId: "SKU",
  itemName: "Nazwa",
  price: "Cena",
} satisfies Partial<Record<keyof ShopifyPrice, string>>;

const shopifyPriceFormatters = {
  price: (value) => formatDecimalValue(value),
} satisfies Partial<Record<keyof ShopifyPrice, (value: unknown) => React.ReactNode>>;

const priceListLabels = {
  name: "Cennik",
  currency: "Waluta",
} satisfies Partial<Record<keyof PriceList, string>>;

const priceLabels = {
  itemId: "Produkt",
  itemName: "Nazwa",
  priceList: "Cennik",
  value: "Wartość",
  comment: "Komentarz",
  activeFrom: "Od",
} satisfies Partial<Record<keyof PriceSheetRow, string>>;

const priceFormatters = {
  activeFrom: (value) => formatDateValue(value),
  value: (value) => formatDecimalValue(value),
} satisfies Partial<Record<keyof PriceSheetRow, (value: unknown) => React.ReactNode>>;

const legacyMegaraxPriceLabels = {
  priceList: "Cennik",
  itemId: "Produkt",
  itemName: "Nazwa",
  gtin: "EAN",
  value: "Wartość",
  comment: "Komentarz",
  activeFrom: "Od",
  createdBy: "Utworzył",
  createdAt: "Utworzono",
  modifiedBy: "Zmodyfikował",
  modifiedAt: "Zmodyfikowano",
} satisfies Partial<Record<keyof LegacyMegaraxPrice, string>>;

const legacyMegaraxPriceFormatters = {
  activeFrom: (value) => formatDateValue(value),
  createdAt: (value) => formatDateTimeValue(value),
  modifiedAt: (value) => formatDateTimeValue(value),
  value: (value) => formatDecimalValue(value),
} satisfies Partial<Record<keyof LegacyMegaraxPrice, (value: unknown) => React.ReactNode>>;

const formatDateValue = (value: unknown) =>
  value instanceof Date ? Serializers.dateOnly.serialize(value) : value ? String(value) : "-";

const formatDateTimeValue = (value: unknown) =>
  value instanceof Date ? Serializers.datetime.serialize(value) : value ? String(value) : "-";

const formatDecimalValue = (value: unknown) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") return value.toFixed(2);

  const toFixed = (value as { toFixed?: unknown }).toFixed;
  if (typeof toFixed === "function") return toFixed.call(value, 2);

  return value.toString();
};
